import { useScheduleEditorCreateItemMutation } from '@graphql/generated';
import { useCallback } from 'react';
import { ScheduleEditorData } from '../types';
import { ScheduleFormDisplay, ScheduleFormDisplaySkeleton } from './form/ScheduleFormDisplay';
import React from 'react';
import { EMPTY_FORM_DATA, ScheduleEditorProps } from '../ScheduleEditor';
import { formatDateAsISO8601TZ, useGetEventTimezoneAndDates } from './form/hooks/useGetEventTimezone';
import { utcToZonedTime } from 'date-fns-tz';
import { SkeletonGroup } from '@shared/components/Skeleton';
import { addError } from '@shared/utils/logger';

export const AddScheduleItem = (props: ScheduleEditorProps & { mode: 'add' }) => {
  const { eventId, onSuccess, onError, onCancel, refetch } = props;
  const [addItem, { loading: isSaving }] = useScheduleEditorCreateItemMutation();
  const { timezone, eventDate, loading } = useGetEventTimezoneAndDates();

  const handleSubmit = useCallback(
    async (data: ScheduleEditorData) => {
      const results = await addItem({
        variables: {
          payload: {
            eventId,
            startTimeISO8601TZ: formatDateAsISO8601TZ(data.startTime, data.timezone),
            endTimeISO8601TZ: formatDateAsISO8601TZ(data.endTime, data.timezone),
            name: data.name,
            description: data.description,
            isPublic: true,
            virtualEventLink: data.virtualEventUrl,
            dressCode: data.dressCode,
          },
        },
        refetchQueries: refetch,
        awaitRefetchQueries: true,
      });

      if (results.data?.createEventScheduleItem) {
        onSuccess(results.data.createEventScheduleItem);
      } else {
        onError?.(results.errors);
      }
    },
    [addItem, eventId, onError, onSuccess, refetch],
  );

  // Use event date/timezone for defaults, falling back to current time in event timezone if date unavailable
  const defaultTime = (() => {
    if (eventDate) return eventDate;
    if (!timezone) return new Date();

    try {
      return utcToZonedTime(new Date(), timezone);
    } catch (error) {
      addError(error, { context: 'AddScheduleItem', message: `Invalid timezone fallback: ${timezone}` });
      return new Date();
    }
  })();

  return (
    <SkeletonGroup
      placeholder={<ScheduleFormDisplaySkeleton />}
      isReady={!loading}
    >
      <ScheduleFormDisplay
        isSaving={isSaving}
        data={{
          ...EMPTY_FORM_DATA,
          // Empty string is required by TimeZonePicker and validation logic
          timezone: timezone ?? '',
          startTime: defaultTime,
          endTime: defaultTime,
        }}
        onSubmit={handleSubmit}
        onCancel={() => {
          onCancel?.();
        }}
      />
    </SkeletonGroup>
  );
};
